/**
 * Application route constants
 * Centralized route paths for type-safe navigation
 */

export const ROUTES = {
  HOME: '/',

  // Authentication routes
  AUTH: {
    LOGIN: '/login',
    REGISTER: '/register',
    FORGOT_PASSWORD: '/forgot-password',
    RESET_PASSWORD: '/reset-password',
    /** Activar cuenta desde el magic link de invitación (pública). */
    ACCEPT_INVITATION: '/join',
    /** Despedida tras eliminar la cuenta (pública, sin sesión). */
    ACCOUNT_DELETED: '/account-deleted',
  },

  // Student role routes
  STUDENT: {
    DASHBOARD: '/student/dashboard',
    STORIES: '/student/stories',
    /** Lista de prácticas de conversación. */
    CONVERSATIONS: '/student/conversations',
    /** Detalle de una práctica de conversación (paralelo a `/student/story/:id`). */
    CONVERSATION: '/student/conversations/:id',
    STORY: '/student/story/:id',
    RESULT: '/student/result/:id',
    PROGRESS: '/student/progress',
    /** Evolución del tiempo de estudio (gráfico). */
    PROGRESS_STUDY_TIME: '/student/progress/study-time',
    /** Tabla de retos del mes en curso (desde Rendimiento mensual). */
    PROGRESS_STORIES_MONTH: '/student/progress/stories-month',
    /** Lista de palabras aprendidas en la semana o el mes (desde Progreso). */
    PROGRESS_LEARNED_WORDS: '/student/progress/learned-words/:period',
    /** Ficha de una palabra aprendida. */
    PROGRESS_LEARNED_WORD: '/student/progress/learned-words/:period/word/:wordId',
    /** Misma ficha que aprendidas, para una entrada de vocabulario guardada. */
    PROGRESS_SAVED_VOCAB: '/student/progress/learned-words/:period/saved/:savedId',
    /** Página independiente de palabras guardadas (desde el menú/⋯). */
    SAVED_WORDS: '/student/saved-words',
    /** Ficha de una palabra guardada (desde la página independiente). */
    SAVED_WORD_DETAIL: '/student/saved-words/:savedId',
    /** Página de recompensas (desde el menú/⋯). */
    REWARDS: '/student/rewards',
    /** Detalle de una recompensa. */
    REWARD_DETAIL: '/student/rewards/:rewardId',
    /** Página de invitar (desde el menú/⋯). */
    INVITE: '/student/invite',
    RANKING: '/student/ranking',
    PROFILE: '/student/profile',
    NOTIFICATIONS: '/student/notifications',
    SETTINGS: '/student/settings',
    LEVEL_TEST: '/student/level-test',
    /** Centro de soporte con contacto y FAQs (desde el menú/⋯). */
    SUPPORT: '/student/support',
    REVIEW: '/student/review',
    /** Wizard de selección de categorías favoritas. ?mode=edit precarga el set actual. */
    ONBOARDING_PREFERENCES: '/student/onboarding/preferences',
  },

  // Company admin role routes
  COMPANY: {
    DASHBOARD: '/company/dashboard',
    EMPLOYEES: '/company/employees',
    EMPLOYEE_DETAIL: '/company/employees/:id',
    REPORTS: '/company/reports',
    REWARDS: '/company/rewards',
    NOTIFICATIONS: '/company/notifications',
    PROFILE: '/company/profile',
    SETTINGS: '/company/settings',
  },

  // Backoffice admin role routes
  BACKOFFICE: {
    DASHBOARD: '/backoffice/dashboard',
    STORIES: '/backoffice/stories',
    STORY_TYPE_SELECT: '/backoffice/stories/select-type',
    STORY_GENERATE: '/backoffice/stories/generate',
    STORY_CREATE: '/backoffice/stories/create',
    STORY_EDIT: '/backoffice/stories/:id/edit',
    STORY_ACTIVITIES: '/backoffice/stories/:storyId/activities',
    ACTIVITY_CREATE: '/backoffice/stories/:storyId/activities/new',
    ACTIVITY_EDIT: '/backoffice/stories/:storyId/activities/:activityId/edit',
    STORY_RESOURCES: '/backoffice/stories/:storyId/resources',
    STORY_RESOURCE_CREATE: '/backoffice/stories/:storyId/resources/new',
    STORY_RESOURCE_EDIT: '/backoffice/stories/:storyId/resources/:resourceId/edit',
    /** Prácticas de conversación. */
    CONVERSATIONS: '/backoffice/conversations',
    CONVERSATIONS_GENERATE: '/backoffice/conversations/generate',
    CONVERSATION_CREATE: '/backoffice/conversations/create',
    CONVERSATION_EDIT: '/backoffice/conversations/:id/edit',
    CONVERSATION_CONTENT: '/backoffice/conversations/:resourceId/content',
    CONVERSATION_CONTENT_CREATE: '/backoffice/conversations/:resourceId/content/new',
    CONVERSATION_CONTENT_EDIT: '/backoffice/conversations/:resourceId/content/:contentId/edit',
    CONVERSATION_ACTIVITIES: '/backoffice/conversations/:resourceId/activities',
    CONVERSATION_ACTIVITY_CREATE: '/backoffice/conversations/:resourceId/activities/new',
    CONVERSATION_ACTIVITY_EDIT: '/backoffice/conversations/:resourceId/activities/:activityId/edit',
    USERS: '/backoffice/users',
    USER_DETAIL: '/backoffice/users/:id',
    COMPANIES: '/backoffice/companies',
    COMPANY_DETAIL: '/backoffice/companies/:id',
    ANALYTICS: '/backoffice/analytics',
    /** Dashboard de preferencias declaradas por los alumnos. */
    ANALYTICS_PREFERENCES: '/backoffice/analytics/preferences',
    CATEGORIES: '/backoffice/categories',
    REWARDS: '/backoffice/rewards',
    NOTIFICATIONS_HEALTH: '/backoffice/notifications/health',
    NOTIFICATIONS: '/backoffice/notifications',
    EMAILS: '/backoffice/emails',
    FEEDBACK: '/backoffice/feedback',
    FEEDBACK_DETAIL: '/backoffice/feedback/:campaignId',
    PROFILE: '/backoffice/profile',
    SETTINGS: '/backoffice/settings',
  },

  // Legal — páginas estáticas públicas servidas desde public/legal (Cloudflare Pages)
  LEGAL: {
    TERMS: '/legal/terminos.html',
    PRIVACY: '/legal/privacidad.html',
    COOKIES: '/legal/cookies.html',
  },

  // Preview (backoffice) — mismo layout que estudiante, abre en nueva pestaña
  PREVIEW: {
    STORY: '/preview/story/:id',
  },
} as const;

/**
 * Helper function to build dynamic routes
 * @example buildRoute(ROUTES.STUDENT.STORY, { id: '123' }) => '/student/story/123'
 */
export function buildRoute(
  route: string,
  params: Record<string, string | number>
): string {
  let builtRoute = route;

  Object.entries(params).forEach(([key, value]) => {
    builtRoute = builtRoute.replace(`:${key}`, String(value));
  });

  return builtRoute;
}
